import { BillSource, BillStatus } from "../types/bill";
import { InvoiceSource, InvoiceStatus } from "../types/invoice";

/**
 * Sources that are created inside Kick and have no external provider behind
 * them. Their `externalStatus` is always NULL, so every consumer must read
 * `status` instead. Integration sources keep their provider-reported
 * `externalStatus` and are compared strictly: a NULL there means the provider
 * state is unknown, and must not silently fall back to our internal status
 * (which is clamped, so it would misreport a provider-paid record as open).
 * QUICKBOOKS is internal despite naming a provider: the import is one-time,
 * and Kick owns the record and its status afterwards.
 */
export const INTERNAL_BILL_SOURCES: BillSource[] = [
    BillSource.IMPORTER,
    BillSource.MCP,
    BillSource.QUICKBOOKS,
];

export const INTERNAL_INVOICE_SOURCES: InvoiceSource[] = [
    InvoiceSource.MANUAL,
    InvoiceSource.IMPORTER,
    InvoiceSource.MCP,
    InvoiceSource.QUICKBOOKS,
];

export function isInternalBillSource(source: BillSource): boolean {
    return INTERNAL_BILL_SOURCES.includes(source);
}

export function isImporterBillSource(source: BillSource): boolean {
    return source === BillSource.IMPORTER || source === BillSource.MCP;
}

export function isInternalInvoiceSource(source: InvoiceSource): boolean {
    return INTERNAL_INVOICE_SOURCES.includes(source);
}

/** Statuses with nothing outstanding, whatever the accrual matches say. */
export const CLOSED_BILL_STATUSES: BillStatus[] = [
    BillStatus.PAID,
    BillStatus.VOID,
];

export const CLOSED_INVOICE_STATUSES: InvoiceStatus[] = [
    InvoiceStatus.PAID,
    InvoiceStatus.VOID,
];

/**
 * Status a bill should be judged and displayed by. Mirrors the backend
 * `billEffectiveStatusSql` used by list filters, stats and aggregations.
 */
export function getBillEffectiveStatus(bill: {
    source: BillSource;
    status: BillStatus;
    externalStatus: BillStatus | null;
}): BillStatus | null {
    return isInternalBillSource(bill.source)
        ? bill.status
        : bill.externalStatus;
}

/** Invoice counterpart of `getBillEffectiveStatus`. */
export function getInvoiceEffectiveStatus(invoice: {
    source: InvoiceSource;
    status: InvoiceStatus;
    externalStatus: InvoiceStatus | null;
}): InvoiceStatus | null {
    return isInternalInvoiceSource(invoice.source)
        ? invoice.status
        : invoice.externalStatus;
}
